import { Box, Group, Text } from '@mantine/core';

import { useTranslation } from 'react-i18next';

import { Badge } from '../../../../../shared/components/UI/Badge/Badge';

import type { BidResponseDocumentType } from '../../../../../shared/entities/BidResponseDocument';
import type {
  BidResponseDocumentSearchResult,
  WorkspaceDocumentSearchResult,
} from '../../../../../shared/entities/Search';
import type { WorkspaceDocumentType } from '../../../../../shared/entities/WorkspaceDocuments';
import {
  isBidResponseDocument,
  isWorkspaceDocument,
} from '../utils/vaultSearchUtils';

type ResultItemLabelProps = {
  result: BidResponseDocumentSearchResult | WorkspaceDocumentSearchResult;
};

export function VaultSearchResultItemLabel({ result }: ResultItemLabelProps) {
  if (isBidResponseDocument(result)) {
    return <BidResponseDocumentResultItemLabel {...result} />;
  }

  if (isWorkspaceDocument(result)) {
    return <WorkspaceDocumentResultItemLabel {...result} />;
  }

  throw new Error('Unknown result type');
}

type BidResponseDocumentResultItemLabelProps = Pick<
  BidResponseDocumentSearchResult,
  | 'bidResponseTitle'
  | 'vaultPropertyValues'
  | 'prettyName'
  | 'type'
  | 'bidResponseResponseDeadline'
>;

function BidResponseDocumentResultItemLabel({
  bidResponseTitle,
  vaultPropertyValues,
  prettyName,
  type,
  bidResponseResponseDeadline,
}: BidResponseDocumentResultItemLabelProps) {
  const formattedDate = new Date(
    bidResponseResponseDeadline,
  ).toLocaleDateString('fr-FR', {
    month: 'long',
    year: 'numeric',
  });

  return (
    <>
      <Group spacing={0} align="center" w="100%">
        <Group noWrap maw="100%" spacing="01" align="center">
          <DocTypeBadge type={type} />

          <Text fw={500} variant="sm" c="gray.9" maw="fit-content" truncate>
            {prettyName}
          </Text>
        </Group>
        <SeparatorPoint />
        <Text fw={400} variant="xs" c="gray.9" truncate>
          {bidResponseTitle}
        </Text>

        <SeparatorPoint />

        <Text fw={400} variant="xs" c="gray.6" transform="capitalize">
          {formattedDate}
        </Text>
      </Group>

      <PropertyValueList vaultPropertyValues={vaultPropertyValues} />
    </>
  );
}

type WorkspaceDocumentResultItemLabelProps = Pick<
  WorkspaceDocumentSearchResult,
  'prettyName' | 'type'
>;

function WorkspaceDocumentResultItemLabel({
  prettyName,
  type,
}: WorkspaceDocumentResultItemLabelProps) {
  return (
    <Group noWrap w="100%" spacing="01">
      <DocTypeBadge type={type} />
      <Text fw={500} variant="sm" c="gray.9" maw="fit-content" truncate>
        {prettyName}
      </Text>
    </Group>
  );
}

type PropertyValueListProps = {
  vaultPropertyValues: BidResponseDocumentSearchResult['vaultPropertyValues'];
};

function PropertyValueList({ vaultPropertyValues }: PropertyValueListProps) {
  if (vaultPropertyValues.length === 0) {
    return null;
  }

  return (
    <Group spacing="01">
      <Text variant="xs" fw={400} c="gray.6">
        Propriété
      </Text>
      {vaultPropertyValues.map((property, index) => (
        <Badge key={index} content={property.value} color={property.color} />
      ))}
    </Group>
  );
}

function SeparatorPoint() {
  return <Box bg="#D9D9D9" h={4} w={4} mx="01" style={{ borderRadius: 8 }} />;
}

function DocTypeBadge({
  type,
}: {
  type: BidResponseDocumentType | WorkspaceDocumentType;
}) {
  const { t } = useTranslation('entities');

  return <Badge content={t(`documentTypes.${type}`)} />;
}
